import { AppointmentTimelinePopup } from '@/components/AppointmentTimeline/Timeline';
import { POSBaseInfoContext } from '@/components/LayoutPOS/Synchronizer';
import { message } from '@/components/Message';
import PosButton from '@/components/PosButton';
import { addGlobalEventListener, removeGlobalScanListener } from '@/components/QRScanner/GlobalScanner';
import { BaseInfoContext } from '@/context/BaseInfoContext';
import { PreOrder } from '@/models/order';
import { ReceivingMode } from '@/models/payment';
import { POSPaymentResponse } from '@/models/pos';
import { getIsPaidOrder } from '@/models/preOrder';
import { UserFullInfo } from '@/models/user';
import { playSound } from '@/utils/sound';
import { CalendarOutlined, MoneyCollectOutlined } from '@ant-design/icons';
import { APIError } from 'api/types';
import React, { useContext, useEffect, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useUpdate } from 'react-use';
import { useImmer } from 'use-immer';
import CheckoutArea from './CheckoutArea';
import CommandHistory from './components/CommandHistory';
import ConsumerDisplay from './components/ConsumerDisplay';
import ScanResponder from './ScanResponder';
import Settlement from './Settlement';
import PaymentSuccess from './Settlement/PaymentSuccess';
import { useSettlementStore } from './Settlement/store/useSettlementStore';
import SkuArea from './SkuArea';
import { Station, StationRef } from './Station';
import { usePreOrderStore } from './store/usePreOrderStore';
import styles from './style.less';
import { UserArea } from './UserArea';
import { fastActionBtnProps } from './utils/ui';

interface Props {
  previewMode?: boolean;
  previews?: ('skuArea' | 'checkoutArea')[];
}

const POS: React.FC<Props> = ({ previewMode = false, previews = [] }: Props) => {
  const forceUpdate = useUpdate();
  // 服务单记录
  const stationRef = useRef<StationRef>();
  const { categoryList, pointRules, cardList } = useContext(POSBaseInfoContext);
  const [, , helpers] = useContext(BaseInfoContext);
  // 待结清单（购物车数据见 usePreOrderStore）
  const preOrder = usePreOrderStore(s => s.preOrder);
  // 结算模式（INVALID 即结算弹窗关闭；结算流程状态见 useSettlementStore）
  const receivingMode = useSettlementStore(s => s.mode);

  // 订单完成信息
  const [orderCompleteInfo, setOrderCompleteInfo] = useImmer<POSPaymentResponse>(null);

  // 页面卸载时清理全局 Store，防止购物车/结算模式残留到下次进入
  useEffect(() => {
    return () => {
      // usePreOrderStore.getState().reset();
      useSettlementStore.getState().reset();
    };
  }, []);

  useEffect(() => {
    const listenerKeys = [
      // 绑定用户
      addGlobalEventListener('actionUserBind', ({ payload }) => {
        usePreOrderStore.getState().bindUser(payload as UserFullInfo);
      }),
      // 用户信息变更, 例发放了优惠券等
      addGlobalEventListener('actionUserUpdate', ({ payload }) => {
        usePreOrderStore.getState().updateUserFullInfo(payload as UserFullInfo);
      }),
      // 解绑用户
      addGlobalEventListener('actionUserUnBind', ({}) => {
        usePreOrderStore.getState().unbindUser();
      }),
      // 用户充值
      addGlobalEventListener('actionUserRecharge', ({ payload }) => {
        usePreOrderStore.getState().bindUser(payload as UserFullInfo);
        useSettlementStore.getState().open(ReceivingMode.RECHARGE);
      }),
      // 线上订单-开始服务
      addGlobalEventListener('actionStartOrder', ({ payload }) => {
        stationRef.current.push(payload as PreOrder);
        // 清空
        usePreOrderStore.getState().resetPreOrder();
      }),
      // 线上订单-结束服务
      addGlobalEventListener('actionCompleteOrder', ({ payload }) => {
        const orderCompleteInfo = payload as POSPaymentResponse;
        setOrderCompleteInfo(() => {
          orderCompleteInfo.type = 'service';
          return orderCompleteInfo;
        });
        stationRef.current.popByOrderSn(orderCompleteInfo.order.orderSn);
      }),
    ];
    return () => {
      removeGlobalScanListener(...listenerKeys);
    };
  }, []);

  const onCheckout = () => {
    if (useSettlementStore.getState().checkout()) {
      playSound('click_light');
    } else {
      message.error('请添加要结账的商品');
      playSound('error');
    }
  };

  // 结账快捷键
  useHotkeys(
    'Enter',
    e => {
      const tagName = (e.target as HTMLElement).tagName.toLowerCase();
      if (tagName === 'input' || tagName === 'textarea') {
        return;
      }

      const hasOpenedModals =
        Array.from(document.querySelectorAll('.ant-modal-wrap')).filter(
          s => window.getComputedStyle(s).getPropertyValue('display') !== 'none'
        ).length > 0;

      if (!hasOpenedModals) {
        onCheckout();
      }
    },
    [onCheckout]
  );

  return (
    <>
      {!previewMode ? (
        <>
          <ScanResponder />
          {orderCompleteInfo ? (
            <PaymentSuccess
              onClose={() => {
                setOrderCompleteInfo(() => null);
              }}
              order={orderCompleteInfo.order}
              user={orderCompleteInfo.user}
              skuList={orderCompleteInfo.skuList}
              transactionList={orderCompleteInfo.transactionList}
              cardUserList={orderCompleteInfo.cardUserList}
              type={orderCompleteInfo.type}
              onDataChange={(user, order) => {
                setOrderCompleteInfo(draft => {
                  draft.user = user || draft.user;
                  draft.order = order || draft.order;
                });
              }}
            />
          ) : null}

          {receivingMode !== ReceivingMode.INVALID && (
            <Settlement
              pointRules={pointRules}
              onReceived={orderCompleteInfoNew => {
                if (useSettlementStore.getState().mode === ReceivingMode.RECHARGE) {
                  // 充值，更新用户信息(余额)
                  usePreOrderStore.getState().rechargeReceived(orderCompleteInfoNew.user);
                } else if (useSettlementStore.getState().mode === ReceivingMode.CART) {
                  // 商品、服务结算
                  // 重置: 清空商品 / 取消关联客户
                  usePreOrderStore.getState().resetPreOrder();
                }
                // 支付完成：关闭结算弹窗
                useSettlementStore.getState().close();
                // 弹出支付结果弹窗
                setOrderCompleteInfo({ type: 'pay', ...orderCompleteInfoNew });
              }}
              onClose={() => {
                useSettlementStore.getState().close();
              }}
            />
          )}
        </>
      ) : null}
      <div className={styles.main}>
        {!previewMode || previews.includes('skuArea') ? (
          <SkuArea
            previewMode={previewMode}
            cardList={cardList}
            categoryList={categoryList}
            actions={
              <>
                {/* <BindUser userFullInfo={preOrder.userFullInfo} /> */}
                <PosButton
                  {...fastActionBtnProps}
                  icon={<MoneyCollectOutlined />}
                  onClick={() => {
                    useSettlementStore.getState().open(ReceivingMode.DIRECT);
                  }}>
                  收款
                </PosButton>
                <CommandHistory />
                <ConsumerDisplay />
                <AppointmentTimelinePopup>
                  <PosButton {...fastActionBtnProps} icon={<CalendarOutlined />} onClick={() => {}}>
                    预约
                  </PosButton>
                </AppointmentTimelinePopup>
              </>
            }
          />
        ) : null}
        {!previewMode || previews.includes('checkoutArea') ? (
          <div className={styles.mainRight}>
            <UserArea />
            <CheckoutArea
              onRequestDeletePreOrder={async preOrder => {
                await stationRef.current?.deletePreOrder(preOrder);

                // 清空preOrder，仅保留关联用户
                usePreOrderStore.getState().resetKeepingUserFullInfo();
              }}
              onRequestPushToStation={async () => {
                const { preOrder } = usePreOrderStore.getState();
                if (preOrder.specifications && preOrder.specifications.length) {
                  try {
                    playSound('click_light');
                    await stationRef.current.push(preOrder);

                    // 清空货品 / 取消关联用户
                    usePreOrderStore.getState().resetPreOrder();
                    message.success('操作成功');
                  } catch (e) {
                    const err = e as APIError;
                    message.error('出错了:' + err.message);
                  }
                } else {
                  message.error('请先添加商品或服务');
                  playSound('error');
                }
              }}
            />
            <Station
              onStationChange={forceUpdate}
              ref={stationRef}
              onPop={preOrder => {
                if (getIsPaidOrder(preOrder)) {
                  // 已入库的订单, 直接显示订单详情
                  helpers.showOrderModal({
                    orderSn: preOrder.orderSn,
                  });
                } else {
                  usePreOrderStore.getState().setPreOrder(preOrder);
                }
              }}
              showOverrideWarning={Boolean(preOrder.specifications && preOrder.specifications.length)}
              onRequestDelete={stationRef.current?.deletePreOrder}
            />
          </div>
        ) : null}
      </div>
    </>
  );
};

export default POS;
